import './bootstrap';
/* AF Library — compiled component styles (must come before app.css) */
import '@af-lib/vue-ui/vue-ui.css';
import '../css/app.css';

import { setGlobalToasterOptions } from '@af-lib/vue-ui';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';

import App from '@/App.vue';
import { loadLocales, useLocales } from '@/composables/useLocales';
import { fetchEnv, setConfig } from '@/config/fetchEnv';
import { i18next, initI18N } from '@/config/i18n';
import icons from '@/config/icons';
import Bugsnag from '@/plugins/bugsnag';
import { i18nPlugin } from '@/plugins/i18n';
import { router } from '@/router';
import { hasSentinel, useRegisterStore } from '@/stores/register';
import { useUserStore } from '@/stores/user';
import { readLocaleCookie } from '@/utils/localeCookie';

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);

// If the af-register sentinel cookie is gone (expired after an hour, or the user
// cleared their cookies) but localStorage still holds stale register state, wipe it
// so the user can start a fresh registration.
const registerStore = useRegisterStore();
if (registerStore.email && !hasSentinel()) {
  registerStore.reset();
}
app.use(icons);

// Invalidate the user store cache when the server issues new tokens.
// The X-Token-Refreshed header is set by withAuthCookies() only on explicit
// refresh_token calls (MFA challenge, ToU acceptance) — not on initial login.
axios.interceptors.response.use(
  response => {
    if (response.headers['x-token-refreshed'] === '1') {
      useUserStore().refresh();
    }
    return response;
  },
  error => {
    // Stale af-auth cookie (account deleted, token expired, etc.) — trigger a full
    // logout so cookies are properly cleared server-side and client-side.
    const url = error?.config?.url ?? '';
    if (error?.response?.status === 401 && !url.includes('/auth/logout')) {
      window.location.href = '/logout';
    }
    return Promise.reject(error);
  }
);

fetchEnv().then(config => {
  setConfig(config);

  if (config.client_token) {
    axios.defaults.headers.common['X-AF-Client'] = config.client_token;
  }

  // Router registered after client token is set so router guard API calls
  // (e.g. branding fetch on invite links) already carry X-AF-Client.
  app.use(router);

  app.use(Bugsnag(config));

  // ── Mixpanel client-side init ────────────────────────────────────────────
  if (config.mixpanel_token) {
    mixpanel.init(config.mixpanel_token, {
      api_host: config.mixpanel_proxy ?? 'https://api.mixpanel.com',
      persistence: 'localStorage',
      cross_subdomain_cookie: true
    });
  }

  const { bucket_s3 } = config;
  // Cookie `locale` — même objet que celui posé par les autres apps, donc une
  // langue choisie ailleurs s'applique dès le premier rendu. La langue du
  // compte (`me.locale_id`) reprend la main juste après, dans le garde du
  // router : voir composables/useUserLocale.
  const cookieLocale = readLocaleCookie()?.iso_code_639;
  const rawLocale = cookieLocale || navigator.language || 'en-US';

  // Les langues proposées viennent de static-data/locales.json, comme dans les
  // autres apps front : on les charge avant i18next, qui a besoin de la liste
  // pour `supportedLngs`.
  loadLocales().then(available => {
    const { toSupportedLocale } = useLocales();
    const locale = toSupportedLocale(rawLocale) ?? 'en-US';
    const supportedLngs = available.map(l => l.iso_code_639);

    return initI18N(bucket_s3, locale, supportedLngs).then(() => {
      // Design-system toaster defaults (mirrors previous vue-toastification config:
      // 8s timeout, top-right, max 5). RTL follows the current language — y compris
      // après coup, la langue pouvant changer sans rechargement (switcher, ou
      // réalignement sur celle du compte dans le garde du router).
      const applyToasterOptions = (lng: string) => {
        const isRtl = ['ar', 'he', 'fa', 'ur'].includes(lng.split('-')[0]);
        setGlobalToasterOptions({ duration: 8000, placement: 'top-end', rtl: isRtl });
      };
      applyToasterOptions(locale);
      i18next.on('languageChanged', applyToasterOptions);

      app.use(i18nPlugin);
      app.mount('#app');
    });
  });
});
